import { useState, useEffect } from 'react';
import data from '../constants.json';
import {
  SiReact, SiNextdotjs, SiTypescript, SiJavascript,
  SiHtml5, SiCss3, SiNodedotjs, SiPython, SiFastapi,
  SiSpringboot, SiPhp, SiMongodb, SiPostgresql,
  SiDocker, SiGooglecloud, SiGithubactions,
  SiCplusplus, SiUnity, SiOpencv,
  SiRedux, SiPrisma, SiVim, SiSocketdotio,
  SiJquery, SiScikitlearn, SiCmake,
} from 'react-icons/si';
// SiCsharp has no Simple Icons entry — FaCode fallback used
import { FaCode, FaExternalLinkAlt } from 'react-icons/fa';

/* ── Tech icon registry ─────────────────────────────────── */
const TECH_ICONS = {
  'React':             { Icon: SiReact,        color: '#61DAFB' },
  'React Routerv7':   { Icon: SiReact,        color: '#CA4245' },
  'Next.js':          { Icon: SiNextdotjs,    color: '#e2e8f0' },
  'TypeScript':       { Icon: SiTypescript,   color: '#3178C6' },
  'JavaScript':       { Icon: SiJavascript,   color: '#F7DF1E' },
  'HTML':             { Icon: SiHtml5,        color: '#E34F26' },
  'CSS':              { Icon: SiCss3,         color: '#1572B6' },
  'Nodejs':           { Icon: SiNodedotjs,    color: '#339933' },
  'Express':          { Icon: SiNodedotjs,    color: '#e2e8f0' },
  'Python':           { Icon: SiPython,       color: '#3776AB' },
  'FastAPI':          { Icon: SiFastapi,      color: '#009688' },
  'SpringBoot':       { Icon: SiSpringboot,   color: '#6DB33F' },
  'PHP':              { Icon: SiPhp,          color: '#777BB4' },
  'MondoDB':          { Icon: SiMongodb,      color: '#47A248' },
  'MongoDB':          { Icon: SiMongodb,      color: '#47A248' },
  'Mern':             { Icon: SiMongodb,      color: '#47A248' },
  'PostgreSQL (Neon)':{ Icon: SiPostgresql,   color: '#336791' },
  'Prisma':           { Icon: SiPrisma,       color: '#e2e8f0' },
  'Docker':           { Icon: SiDocker,       color: '#2496ED' },
  'GitHub Actions':   { Icon: SiGithubactions,color: '#2088FF' },
  'Google Cloud Run': { Icon: SiGooglecloud,  color: '#4285F4' },
  'Cloud Scheduler':  { Icon: SiGooglecloud,  color: '#4285F4' },
  'C++':              { Icon: SiCplusplus,    color: '#00599C' },
  // C# has no Simple Icons entry — FaCode fallback used
  'Unity':            { Icon: SiUnity,        color: '#e2e8f0' },
  'Open CV':          { Icon: SiOpencv,       color: '#5C3EE8' },
  'Redux':            { Icon: SiRedux,        color: '#764ABC' },
  'Vim':              { Icon: SiVim,          color: '#019733' },
  'Socket.io':        { Icon: SiSocketdotio,  color: '#e2e8f0' },
  'Scikit-learn':     { Icon: SiScikitlearn,  color: '#F7931E' },
  'jQuery':           { Icon: SiJquery,       color: '#0769AD' },
  'Cmake':            { Icon: SiCmake,        color: '#064F8C' },
};

const ACCENT_COLORS = [
  '#00f5ff', '#ff00de', '#00ff88',
  '#7c3aed', '#f59e0b', '#00f5ff',
  '#ff00de', '#00ff88', '#7c3aed',
];

/* ── Single tech chip ───────────────────────────────────── */
const TechChip = ({ tech, accent }) => {
  const info = TECH_ICONS[tech];
  const Icon = info?.Icon;
  const iconColor = info?.color;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all duration-200 select-none"
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        background: 'rgba(0,0,0,0.3)',
        border: `1px solid ${accent}25`,
        color: '#94a3b8',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.color = '#e2e8f0';
        e.currentTarget.style.background = `${accent}12`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = `${accent}25`;
        e.currentTarget.style.color = '#94a3b8';
        e.currentTarget.style.background = 'rgba(0,0,0,0.3)';
      }}
    >
      {Icon
        ? <Icon size={11} style={{ color: iconColor, flexShrink: 0 }} />
        : <FaCode size={10} style={{ color: accent, opacity: 0.7, flexShrink: 0 }} />
      }
      {tech}
    </span>
  );
};

/* ── Projects section ───────────────────────────────────── */
const Projects = () => {
  const [showAll, setShowAll] = useState(false);

  const allProjects     = Object.values(data.data);
  const visibleProjects = showAll ? allProjects : allProjects.slice(0, 5);
  const hasMore         = allProjects.length > 5;

  /* Reveal newly mounted cards when "View All" is clicked */
  useEffect(() => {
    if (!showAll) return;
    const id = setTimeout(() => {
      document.querySelectorAll('article.reveal:not(.revealed)').forEach((el) => {
        el.classList.add('revealed');
      });
    }, 30);
    return () => clearTimeout(id);
  }, [showAll]);

  return (
    <section id="projects" className="py-20 px-2">

      {/* Heading */}
      <div className="reveal mb-14">
        <p className="text-[#00ff88] text-xs tracking-[0.25em] uppercase mb-2"
           style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          // things I've built
        </p>
        <h2
          className="section-title text-4xl font-black mb-4"
          style={{
            background: 'linear-gradient(135deg, #00f5ff, #00ff88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Projects
        </h2>
        <div
          className="neon-line w-28"
          style={{ background: 'linear-gradient(90deg, transparent, #00ff88 50%, transparent)' }}
        />
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-5">
        {visibleProjects.map((project, index) => {
          const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];

          return (
            <article
              key={index}
              className="reveal rounded-xl overflow-hidden transition-all duration-300"
              style={{
                transitionDelay: `${(index % 5) * 0.07}s`,
                background: 'rgba(10, 10, 35, 0.92)',
                boxShadow: `inset 4px 0 0 ${accent}`,
                border: `1px solid ${accent}18`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.border = `1px solid ${accent}55`;
                e.currentTarget.style.boxShadow = `inset 4px 0 0 ${accent}, 0 8px 30px ${accent}14`;
                e.currentTarget.style.transform = 'translateX(5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.border = `1px solid ${accent}18`;
                e.currentTarget.style.boxShadow = `inset 4px 0 0 ${accent}`;
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              {/* ── Card header ── */}
              <div
                className="flex items-center justify-between gap-4 px-5 py-3"
                style={{ borderBottom: `1px solid ${accent}12` }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="text-xs font-bold flex-shrink-0 tabular-nums"
                    style={{ color: accent, fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3
                    className="font-bold text-slate-100 truncate"
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                    }}
                  >
                    {project.name}
                  </h3>
                </div>

                <button
                  onClick={() => window.open(project.link, '_blank')}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: accent,
                    border: `1px solid ${accent}40`,
                    background: `${accent}0a`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${accent}1e`;
                    e.currentTarget.style.borderColor = accent;
                    e.currentTarget.style.boxShadow = `0 0 10px ${accent}35`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `${accent}0a`;
                    e.currentTarget.style.borderColor = `${accent}40`;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <FaExternalLinkAlt size={10} />
                  <span className="hidden sm:inline">View</span>
                </button>
              </div>

              {/* ── Card body: image + description ── */}
              <div>
                {/* Thumbnail — centered, fixed small height */}
                <div
                  className="flex items-center justify-center overflow-hidden"
                  style={{ height: 160, borderBottom: `1px solid ${accent}12`, background: 'rgba(0,0,0,0.25)' }}
                >
                  <img
                    src={project.img}
                    alt={project.name}
                    onClick={() => window.open(project.link, '_blank')}
                    className="transition-transform duration-500 hover:scale-105"
                    style={{ objectFit: 'contain', maxHeight: '140px', maxWidth: '90%', display: 'block', cursor: 'pointer' }}
                  />
                </div>

                {/* Description */}
                <div className="px-5 py-4">
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {project.dis}
                  </p>
                </div>
              </div>

              {/* ── Tech stack ── */}
              <div
                className="px-5 py-3 flex flex-wrap gap-2"
                style={{ borderTop: `1px solid ${accent}12` }}
              >
                {project.stack.map((tech, ti) => (
                  <TechChip key={ti} tech={tech} accent={accent} />
                ))}
              </div>
            </article>
          );
        })}
      </div>

      {/* View All / Show Less toggle */}
      {hasMore && (
        <div className="text-center mt-10">
          <button
            className="neon-btn"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll
              ? '← Show Less'
              : `View All ${allProjects.length} Projects →`}
          </button>
        </div>
      )}
    </section>
  );
};

export default Projects;
